// Help Assistant (features/help-assistant/PLAN_2026-10-05.md) — the in-app "? Help" button and panel.
// Sits on top of the app: a body-level button + panel that survive re(). Reads only small context signals from the
// HUD bridge (role, clinic-head, current tab, language). Hidden entirely until the help library exists in the DB,
// so this module is safe to ship before the H1 migration is applied.
import { injectHelpStyles } from './styles.js';
import { withClear } from '../search-clear/index.js';
import { buildIndex, search, isStrong } from './match.js';
import * as data from './data.js';
import { openReport } from './report.js';

const H = () => window.HUD;
const T = (k, v) => { const s = H()?.T?.('help_' + k, v); return s && s !== 'help_' + k ? s : k; };
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const st = {
  ready: false, open: false, mode: 'phone', view: 'start', stack: [], current: null,
  query: '', lastQuery: '', pinned: false, closedAt: 0, index: null, library: [], requests: [], ideaDraft: '',
  leftOut: false,   // true on the re-search results after "Search again": the note says the rejected video is not in them
  rejected: new Set(),   // codes of the videos this parent said "No" to: left out of every search / chip until the panel starts fresh or a request is sent (device only, never saved)
};
let panel, body, titleEl, pinBtn;
// H2 (R-20261022-0034): readiness is remembered per user for 7 days, so on a repeat visit the "?" is drawn in the FIRST render, before the library request returns.
// It is only a hint to draw the button: the panel itself still needs the real boot (open() waits for it), and a boot that finds the library empty or missing clears it.
const READY_KEY = 'hud_help_ready', READY_TTL = 7 * 24 * 3600 * 1000;
function cachedReady() {
  try {
    const v = JSON.parse(localStorage.getItem(READY_KEY) || 'null'); const u = window.HUD?.session?.id;
    return !!(v && u && v.u === u && Date.now() - (v.at || 0) < READY_TTL);
  } catch (_) { return false; }
}
function writeReady(u) { try { localStorage.setItem(READY_KEY, JSON.stringify({ u, at: Date.now() })); } catch (_) {} }
function clearReady() { try { localStorage.removeItem(READY_KEY); } catch (_) {} }
const showable = () => st.ready || cachedReady();
// A tap on a cached-ready button can come before the library has arrived: open() waits for the real boot (at most 12 s) instead of doing nothing.
function whenReady() { if (st.ready) return Promise.resolve(); return new Promise(res => { (st.waiters = st.waiters || []).push(res); setTimeout(res, 12000); }); }
function flushReady() { const w = st.waiters || []; st.waiters = []; w.forEach(f => f()); }

// ── context (read-only signals from the app) ──
function ctx() {
  const h = H() || {}; const S = h.S || {}; const session = h.session || {};
  const head = (S._clinics || []).some(c => c.clinic_id === S.activeClinicId && c.role === 'head');
  const role = session.role === 'parent' ? 'parent' : head ? 'clinic-head' : 'specialist';
  const lang = (h._langNow || h._lang || 'en') === 'he' ? 'he' : 'en';
  const w = window.innerWidth;
  const ua = navigator.userAgent;
  const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Other';
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches;
  return {
    role, lang, screen: S.activeTab || 'dashboard', isAdmin: !!session.isAdmin,
    audiences: role === 'parent' ? ['parent'] : role === 'clinic-head' ? ['specialist', 'clinic-head'] : ['specialist'],
    device: `${os} · ${w < 768 ? 'phone' : w < 1024 ? 'tablet' : 'desktop'}${standalone ? ' · app' : ''}`,
    build: (document.querySelector('script[src*="app.bundle.js"]')?.src.split('?')[1]) || location.host,
  };
}
const L = o => (o && typeof o === 'object' ? (o[ctx().lang] || o.en || '') : (o || ''));
const hasSidebar = () => !!document.querySelector('.spec-dt-shell');
function layoutMode() {
  const w = window.innerWidth;
  if (w < 768) return 'phone';
  if (w >= 1024 && hasSidebar()) return 'side';
  return 'float';
}

// ── mount ──
function mount() {
  injectHelpStyles();
  panel = document.createElement('div');
  panel.className = 'hh-panel'; panel.hidden = true; panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'false');
  panel.innerHTML = `<div class="hh-head"><button class="hh-ib" type="button" data-a="back" aria-label="${esc(T('back'))}">←</button>
    <h3></h3><button class="hh-ib hh-pin" type="button" data-a="pin" aria-pressed="false" title="${esc(T('pin_title'))}">📌</button>
    <button class="hh-ib" type="button" data-a="close" aria-label="${esc(T('close'))}">✕</button></div>
    <div class="hh-dockhint">${esc(T('docked_hint'))}</div><div class="hh-body"></div>`;
  body = panel.querySelector('.hh-body'); titleEl = panel.querySelector('h3'); pinBtn = panel.querySelector('[data-a=pin]');
  panel.querySelector('[data-a=back]').onclick = back;
  panel.querySelector('[data-a=close]').onclick = () => close({ reset: true });
  pinBtn.onclick = () => { st.pinned = !st.pinned; pinBtn.setAttribute('aria-pressed', String(st.pinned)); applyDock(); };
  document.body.append(panel);
  // close on any tap/click outside (tablet + computer), unless pinned
  document.addEventListener('pointerdown', ev => {
    if (!st.open || st.mode === 'phone' || st.pinned) return;
    const t = ev.target;
    if (panel.contains(t) || t.closest?.('.hh-lightbox,.hh-report,[data-help-open]')) return;
    close();
  }, true);
  document.addEventListener('keydown', ev => {
    if (ev.key === 'Escape') {
      const lb = document.querySelector('.hh-lightbox'); if (lb) { lb.remove(); return; }
      if (st.open && !document.querySelector('.hh-report')) close();
    } else if (ev.key === '?' && !st.open && hasSidebar() && !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName) && !document.activeElement?.isContentEditable) {
      open();
    }
  });
  window.addEventListener('resize', () => { if (st.open) { setMode(); applyDock(); } });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshRequests(); });
  setInterval(refreshRequests, 5 * 60 * 1000);
}

function setMode() {
  st.mode = layoutMode();
  panel.classList.remove('hh-phone', 'hh-float', 'hh-side');
  panel.classList.add('hh-' + st.mode);
  panel.setAttribute('aria-modal', String(st.mode === 'phone'));
}
function applyDock() {
  // Pinned on a wide computer screen: the app narrows to sit beside the panel (nothing hidden).
  const dock = st.open && st.pinned && st.mode === 'side' && window.innerWidth >= 1360;
  document.body.classList.toggle('hh-docked', dock);
}

// ── open / close ──
export async function open(opts = {}) {
  if (!st.ready) { await whenReady(); if (!st.ready) return; }
  setMode();
  st.open = true; panel.hidden = false; document.body.classList.add('hh-open');
  const resume = st.closedAt && Date.now() - st.closedAt < 10 * 60 * 1000 && st.view !== 'start' && !opts.requestId;
  if (opts.requestId) {
    await refreshRequests();
    const r = st.requests.find(x => x.id === opts.requestId);
    st.rejected.clear(); st.leftOut = false; go(r ? 'request' : 'start', true, r);
  } else if (resume) render();
  else { st.rejected.clear(); st.leftOut = false; go('start', true); }
  applyDock();
  panel.querySelector('input,textarea,button.hh-choice')?.focus?.({ preventScroll: true });
}
export function close(opts = {}) {
  if (!st.open) return;
  pauseVideos();
  st.open = false; st.closedAt = opts.reset ? 0 : Date.now();   // ✕ starts fresh next time; an outside tap resumes
  panel.hidden = true; document.body.classList.remove('hh-open', 'hh-docked');
}
function pauseVideos() { panel?.querySelectorAll('video').forEach(v => { try { v.pause(); } catch (_) {} }); }
function go(view, reset, cur) {
  pauseVideos();
  if (view === 'start') st.leftOut = false;
  if (reset) st.stack = []; else st.stack.push({ view: st.view, current: st.current });
  st.view = view; if (cur !== undefined) st.current = cur;
  render();
}
function back() {
  const p = st.stack.pop();
  if (!p) { close({ reset: true }); return; }
  pauseVideos(); st.view = p.view; st.current = p.current; render();
}

// ── requests + badge ──
async function refreshRequests() {
  if (!st.ready) return;
  const r = await data.myRequests();
  if (r) { st.requests = r; updateBadge(); if (st.open && st.view === 'start') render(); }
}
function unseenAnswers() { return st.requests.filter(r => r.answered_at && !r.seen_at); }
function updateBadge() {
  const n = unseenAnswers().length;
  document.querySelectorAll('[data-help-dot]').forEach(d => { d.style.display = n ? '' : 'none'; d.textContent = String(n); });
}
// Shared mount: puts the "?" into one host element (once, idempotent). Any shell that draws its own header marks it
// data-help-host (the app admin shell; the parent desktop nav can do the same) and calls HUD_HELP.mount(host) at render;
// boot() mounts into every host that already exists when Help becomes ready, so no re() is needed.
const HOSTS = '#header .header-inner,[data-help-host]';
export function mount_(host) {
  if (!showable() || !host) return false;
  if (!host.querySelector('.hh-hb')) {
    const before = host.querySelector(':scope > [data-help-before]');   // a host can name the element the ? goes in front of (the admin avatar)
    if (before) host.insertBefore(headerButton(), before); else host.appendChild(headerButton());
  }
  return true;
}
function mountAll() { document.querySelectorAll(HOSTS).forEach(mount_); }
// The header "?" button. index.html's renderHeader() asks for a fresh one on every render (re() rebuilds the header).
export function headerButton() {
  const n = unseenAnswers().length;
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'hh-hb'; b.title = T('button');
  b.setAttribute('data-help-open', '1'); b.setAttribute('aria-haspopup', 'dialog'); b.setAttribute('aria-label', T('button'));
  b.innerHTML = `<span aria-hidden="true">?</span><span class="hh-dot" data-help-dot="1"${n ? '' : ' style="display:none"'}>${n}</span>`;
  b.onclick = () => open();
  return b;
}
function statusPill(r) {
  if (r.answered_at || r.status === 'answered') return [r.answer_kind === 'not_possible' ? T('st_suggested') : T('st_answer_ready'), 'ok'];
  if (r.status === 'idea_logged' || r.kind === 'idea') return [T('st_idea_received'), ''];
  if (r.status === 'bug_fixed') return [T('st_fixed'), 'ok'];
  if (r.kind === 'bug' || r.status === 'bug_open') return [T('st_problem_reported'), 'bad'];
  if (r.urgent || r.status === 'human') return [T('st_urgent'), 'warn'];
  return [T('st_with_support'), ''];
}

// ── views ──
function render() {
  const c = ctx(); const v = st.view;
  titleEl.textContent = T('title_' + v);
  const html = VIEWS[v] ? VIEWS[v](c) : '';
  body.innerHTML = html;
  body.dir = c.lang === 'he' ? 'rtl' : 'ltr';
  (WIRE[v] || (() => {}))(c);
  // generic wiring
  body.querySelectorAll('[data-open]').forEach(x => x.onclick = () => openAnswer(x.dataset.open));
  body.querySelectorAll('[data-go]').forEach(x => x.onclick = () => go(x.dataset.go));
}
const popular = c => {
  const pool = st.library.filter(e => c.audiences.includes(e.audience) && !st.rejected.has(e.code));
  const here = pool.filter(e => (e.screens || []).includes(c.screen));
  return (here.length ? here : pool).slice(0, 4);
};
const VIEWS = {
  start: c => {
    // Only what needs attention stays on the start screen (unopened answers + requests still being worked on, newest
    // 2). Everything else folds into a closed "Past requests" drawer, so the screen never grows.
    const needs = r => (r.answered_at && !r.seen_at) || (!r.answered_at && ['new', 'working', 'held', 'human'].includes(r.status));
    const now = st.requests.filter(needs).slice(0, 2);
    const past = st.requests.filter(r => !now.includes(r));
    const row = r => { const [lbl, cls] = statusPill(r); return `<button type="button" data-req="${esc(r.id)}"><span>${esc(r.text)}</span><span class="hh-pill ${cls}">${esc(lbl)}</span></button>`; };
    return `<p style="margin:0">${esc(T('start_prompt'))}</p>
      <div class="hh-choices">
        <button type="button" class="hh-choice" data-go="howto"><span class="hh-ci">?</span><span><b>${esc(T('ch_howto'))}</b><small>${esc(T('ch_howto_sub'))}</small></span></button>
        <button type="button" class="hh-choice" data-go="bug"><span class="hh-ci warn">!</span><span><b>${esc(T('ch_bug'))}</b><small>${esc(T('ch_bug_sub'))}</small></span></button>
        <button type="button" class="hh-choice" data-go="idea"><span class="hh-ci idea">✦</span><span><b>${esc(T('ch_idea'))}</b><small>${esc(T('ch_idea_sub'))}</small></span></button>
      </div>
      ${now.length ? `<div class="hh-label">${esc(T('your_requests'))}</div><div class="hh-mine">${now.map(row).join('')}</div>` : ''}
      ${past.length ? `<details class="hh-past"${st.pastOpen ? ' open' : ''}><summary>${esc(T('past_requests', { v: past.length }))}</summary><div class="hh-mine">${past.map(row).join('')}</div></details>` : ''}
      <p class="hh-muted">${esc(T('urgent_q'))} <button type="button" class="hh-link" data-go="urgent">${esc(T('urgent_link'))}</button></p>
      ${c.isAdmin ? `<button type="button" class="hh-btn ghost sm" data-a="report" style="align-self:flex-start">${esc(T('admin_report'))}</button>` : ''}`;
  },
  howto: c => `<form class="hh-search" data-a="ask"><input class="hh-input" id="hh-q" autocomplete="off" placeholder="${esc(T('search_ph'))}" value="${esc(st.query)}" aria-label="${esc(T('search_ph'))}"><button class="hh-btn" type="submit">${esc(T('ask'))}</button></form>
      ${st.leftOut && st.rejected.size ? `<div class="hh-left" data-a="left-out"><span aria-hidden="true">&#9432;</span><span>${esc(T('left_out'))}</span></div>` : ''}
      <div id="hh-sugg"></div>
      <p class="hh-muted">${esc(T('privacy_note'))}</p>
      <div class="hh-label">${esc(T('popular_here'))}</div>
      <div class="hh-chips">${popular(c).map(e => `<button class="hh-chip" type="button" data-open="${esc(e.code)}">${esc(L(e.q))}</button>`).join('')}</div>`,
  answer: c => {
    const e = st.current; if (!e) return '';
    const steps = (e.steps?.[c.lang]?.length ? e.steps[c.lang] : e.steps?.en) || [];
    const src = data.videoUrl(e.video?.[c.lang] || e.video?.en);
    const tip = L(e.tip);
    return `<h3 style="margin:0;font:500 20px 'Fraunces',Georgia,serif">${esc(L(e.q))}</h3>
      ${src ? `<div class="hh-vwrap"><video class="hh-video ${e.device === 'phone' ? 'portrait' : ''}" src="${esc(src)}" controls playsinline preload="metadata"></video><button type="button" class="hh-big" data-a="big">⤢ ${esc(T('watch_large'))}</button></div>`
            : `<div class="hh-novideo">${esc(e.unavailable ? L(e.unavailable) : T('no_video'))}</div>`}
      ${renderSteps(steps)}
      ${tip ? `<div class="hh-tip"><b>${esc(T('tip'))}</b> ${esc(tip)}</div>` : ''}
      <div class="hh-ask"><b>${esc(T('did_help'))}</b><span style="display:flex;gap:8px"><button class="hh-btn sm" type="button" data-a="yes">👍 ${esc(T('yes'))}</button><button class="hh-btn sm ghost" type="button" data-a="no">👎 ${esc(T('no'))}</button></span></div>`;
  },
  choose: c => `<p style="margin:0">${esc(T('close_to', { v: st.lastQuery }))}</p>
      <div class="hh-list">${(st.current || []).map(x => `<button type="button" data-open="${esc(x.e.code)}"><span class="q">${esc(L(x.e.q))}</span><span class="hh-match">${Math.round(x.s * 100)}%</span></button>`).join('')}</div>
      <button class="hh-btn ghost" type="button" data-go="nomatch">${esc(T('none_of_these'))}</button>`,
  nomatch: c => `<p style="margin:0">${esc(T('no_answer_yet', { v: st.lastQuery }))}</p>
      <div class="hh-choices">
        <button type="button" class="hh-choice" data-a="nm-ask"><span class="hh-ci">?</span><span><b>${esc(T('nm_ask'))}</b><small>${esc(T('nm_ask_sub'))}</small></span></button>
        <button type="button" class="hh-choice" data-a="nm-idea"><span class="hh-ci idea">✦</span><span><b>${esc(T('nm_idea'))}</b><small>${esc(T('nm_idea_sub'))}</small></span></button>
      </div>`,
  helped: () => `<div class="hh-note ok"><b>${esc(T('glad'))}</b><br>${esc(T('glad_sub'))}</div><button class="hh-btn" type="button" data-go="start">${esc(T('ask_else'))}</button>`,
  nothelp: c => `<p style="margin:0">${esc(T('sorry_not'))}</p>
      <div class="hh-field"><label class="hh-label" for="hh-more">${esc(T('own_words'))}</label><textarea class="hh-textarea" id="hh-more">${esc(st.lastQuery)}</textarea></div>
      <p class="hh-muted">${esc(T('auto_context'))}</p>
      <button class="hh-btn" type="button" data-a="search-again">${esc(T('search_again'))}</button>
      <button class="hh-btn ghost" type="button" data-a="send-more">${esc(T('send_support'))}</button>`,
  bug: c => `<div class="hh-field"><label class="hh-label" for="hh-bug">${esc(T('bug_what'))}</label><textarea class="hh-textarea" id="hh-bug" placeholder="${esc(T('bug_ph'))}"></textarea></div>
      <div class="hh-field"><span class="hh-label">${esc(T('bug_often'))}</span><div class="hh-seg" role="group"><button type="button" aria-pressed="true" data-often="once">${esc(T('often_once'))}</button><button type="button" aria-pressed="false" data-often="sometimes">${esc(T('often_sometimes'))}</button><button type="button" aria-pressed="false" data-often="always">${esc(T('often_always'))}</button></div></div>
      <div class="hh-note" style="font-size:13px">${esc(T('bug_auto', { v: c.screen }))}</div>
      <p class="hh-muted">${esc(T('privacy_note'))}</p>
      <button class="hh-btn" type="button" data-a="send-bug">${esc(T('send_report'))}</button>`,
  idea: c => `<div class="hh-field"><label class="hh-label" for="hh-idea">${esc(T('idea_what'))}</label><textarea class="hh-textarea" id="hh-idea" placeholder="${esc(T('idea_ph'))}">${esc(st.ideaDraft)}</textarea></div>
      <div id="hh-ideahint"></div>
      <div class="hh-field"><span class="hh-label">${esc(T('idea_importance'))}</span><div class="hh-seg" role="group"><button type="button" aria-pressed="true" data-imp="nice">${esc(T('imp_nice'))}</button><button type="button" aria-pressed="false" data-imp="important">${esc(T('imp_important'))}</button><button type="button" aria-pressed="false" data-imp="must">${esc(T('imp_must'))}</button></div></div>
      <button class="hh-btn" type="button" data-a="send-idea">${esc(T('send_idea'))}</button>`,
  urgent: () => `<div class="hh-field"><label class="hh-label" for="hh-urg">${esc(T('urgent_what'))}</label><textarea class="hh-textarea" id="hh-urg" placeholder="${esc(T('urgent_ph'))}"></textarea></div>
      <p class="hh-muted">${esc(T('urgent_note'))}</p>
      <button class="hh-btn" type="button" data-a="send-urgent">${esc(T('send'))}</button>`,
  thanks: () => `<div class="hh-note info">${st.current?.html || ''}</div><button class="hh-btn" type="button" data-go="start">${esc(T('done'))}</button>`,
  request: c => {
    const r = st.current; if (!r) return '';
    const [lbl, cls] = statusPill(r);
    const ans = L(data.visibleAnswer(r));   // a held or waiting draft is never shown (data.SENT_STATUSES)
    return `<div class="hh-note"><b>${esc(r.text)}</b><br><span class="hh-pill ${cls}" style="margin-top:6px">${esc(lbl)}</span></div>
      ${r.answer_kind === 'not_possible' ? `<div class="hh-note">${esc(T('not_possible_msg'))}</div>` : ''}
      ${ans ? `<div class="hh-note ok" style="white-space:pre-wrap">${esc(ans)}</div>` : ''}
      ${data.visibleCode(r) ? `<button class="hh-btn" type="button" data-open="${esc(data.visibleCode(r))}">▶ ${esc(T('watch_answer'))}</button>` : ''}
      ${!r.answered_at && !ans ? `<p class="hh-muted">${esc(T('we_will_notify'))}</p>` : ''}`;
  },
};
// Steps are either a plain list, or — when a task can be done in several ways — a list of ways:
// [{ title: 'From the calendar', at: 12, steps: [...] }, …]. "at" (seconds) jumps the video to that way's chapter.
function renderSteps(steps) {
  if (!steps.length) return '';
  const ways = steps.filter(x => x && typeof x === 'object');
  if (!ways.length) return `<div class="hh-label">${esc(T('steps_written'))}</div><ol class="hh-steps">${steps.map(s => `<li>${esc(s)}</li>`).join('')}</ol>`;
  return `<div class="hh-label">${esc(T('ways_count', { v: ways.length }))}</div>
    <div class="hh-ways">${ways.map((w, i) => `<div class="hh-way"><div class="hh-way-h"><b>${esc(T('way_n', { v: i + 1 }))} ${esc(w.title || '')}</b>${Number.isFinite(w.at) ? `<button type="button" class="hh-link" data-at="${w.at}">▶ ${esc(T('watch_this_way'))}</button>` : ''}</div>
      <ol class="hh-steps">${(w.steps || []).map(s => `<li>${esc(s)}</li>`).join('')}</ol></div>`).join('')}</div>`;
}
const WIRE = {
  start: c => {
    body.querySelectorAll('[data-req]').forEach(x => x.onclick = () => openRequest(x.dataset.req));
    const past = body.querySelector('.hh-past'); if (past) past.ontoggle = () => { st.pastOpen = past.open; };
    body.querySelector('[data-a=report]')?.addEventListener('click', () => openReport({ T, esc, L, videoUrl: data.videoUrl }));
  },
  howto: c => {
    const q = body.querySelector('#hh-q');
    const sugg = () => {
      st.query = q.value; const box = body.querySelector('#hh-sugg'); const s = q.value.trim();
      if (s.length < 3) { box.innerHTML = ''; return; }
      const res = search(st.index, s, c.audiences, 3, st.rejected);
      box.innerHTML = res.length
        ? `<div class="hh-label" style="margin-bottom:6px">${esc(T('similar'))}</div><div class="hh-list">${res.map(x => `<button type="button" data-open="${esc(x.e.code)}"><span class="q">${esc(L(x.e.q))}</span><span class="hh-match">${Math.round(x.s * 100)}%</span></button>`).join('')}</div>`
        : `<p class="hh-muted">${esc(T('no_similar'))}</p>`;
      box.querySelectorAll('[data-open]').forEach(x => x.onclick = () => { st.lastQuery = s; openAnswer(x.dataset.open); });
    };
    q.oninput = sugg; sugg(); withClear(q);   // F-107: attached after render (the field is part of an HTML string)
    body.querySelector('[data-a=ask]').onsubmit = ev => { ev.preventDefault(); ask(q.value, c); };
    setTimeout(() => q.focus({ preventScroll: true }), 30);
  },
  answer: c => {
    const e = st.current;
    body.querySelector('[data-a=big]')?.addEventListener('click', () => lightbox(body.querySelector('video')?.src));
    body.querySelectorAll('[data-at]').forEach(x => x.onclick = () => { const v = body.querySelector('video'); if (!v) return; v.currentTime = +x.dataset.at; v.play().catch(() => {}); v.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); });
    body.querySelector('[data-a=yes]').onclick = () => { data.feedback(e.code, true, st.lastQuery && st.lastQuery !== L(e.q) ? st.lastQuery : null, c.lang); go('helped'); };
    body.querySelector('[data-a=no]').onclick = () => { data.feedback(e.code, false, st.lastQuery || null, c.lang); st.notHelpedBy = e.code; st.rejected.add(e.code); go('nothelp'); };
  },
  nomatch: c => {
    body.querySelector('[data-a=nm-ask]').onclick = () => send('howto', st.lastQuery, {}, T('sent_howto'));
    body.querySelector('[data-a=nm-idea]').onclick = () => send('idea', st.lastQuery, { from: 'howto' }, T('sent_idea_from_howto'));
  },
  nothelp: () => {
    body.querySelector('[data-a=search-again]').onclick = () => {
      // Shape 1: back to the search with the parent's own words; the rejected video stays out (st.rejected). Submits nothing.
      const box = body.querySelector('#hh-more'); const v = box.value.trim();
      if (!v) { box.focus(); return; }
      st.query = v; st.lastQuery = v; st.leftOut = true;
      go('howto');
    };
    body.querySelector('[data-a=send-more]').onclick = () => {
      const v = body.querySelector('#hh-more').value.trim() || T('not_answered_by', { v: st.notHelpedBy || '' });
      send('howto', v, { not_helped_by: st.notHelpedBy || null }, T('sent_howto'));
    };
  },
  bug: () => {
    const seg = [...body.querySelectorAll('[data-often]')];
    seg.forEach(b => b.onclick = () => seg.forEach(x => x.setAttribute('aria-pressed', String(x === b))));
    body.querySelector('[data-a=send-bug]').onclick = () => {
      const v = body.querySelector('#hh-bug').value.trim();
      if (!v) { body.querySelector('#hh-bug').focus(); return; }
      const often = seg.find(x => x.getAttribute('aria-pressed') === 'true')?.dataset.often;
      send('bug', v, { how_often: often }, T('sent_bug'));
    };
  },
  idea: c => {
    const box = body.querySelector('#hh-idea'); const hint = body.querySelector('#hh-ideahint');
    const upd = () => {
      st.ideaDraft = box.value;
      const res = box.value.trim().length > 6 ? search(st.index, box.value, c.audiences, 1, st.rejected) : [];
      hint.innerHTML = res[0] && res[0].s >= 0.55 ? `<div class="hh-note ok" style="font-size:14px"><b>${esc(T('maybe_possible'))}</b><br><button type="button" class="hh-link" data-open="${esc(res[0].e.code)}">${esc(L(res[0].e.q))}</button></div>` : '';
      hint.querySelectorAll('[data-open]').forEach(x => x.onclick = () => openAnswer(x.dataset.open));
    };
    box.oninput = upd; upd();
    const seg = [...body.querySelectorAll('[data-imp]')];
    seg.forEach(b => b.onclick = () => seg.forEach(x => x.setAttribute('aria-pressed', String(x === b))));
    body.querySelector('[data-a=send-idea]').onclick = () => {
      const v = box.value.trim(); if (!v) { box.focus(); return; }
      const imp = seg.find(x => x.getAttribute('aria-pressed') === 'true')?.dataset.imp;
      st.ideaDraft = '';
      send('idea', v, { importance: imp }, T('sent_idea'));
    };
  },
  urgent: () => {
    body.querySelector('[data-a=send-urgent]').onclick = () => {
      const v = body.querySelector('#hh-urg').value.trim(); if (!v) { body.querySelector('#hh-urg').focus(); return; }
      send('urgent', v, {}, T('sent_urgent'));
    };
  },
};

function ask(q, c) {
  q = String(q || '').trim(); if (!q) return; st.lastQuery = q;
  const res = search(st.index, q, c.audiences, 3, st.rejected);
  if (isStrong(res)) openAnswer(res[0].e.code);
  else if (res.length) go('choose', false, res);
  else go('nomatch');
}
async function openAnswer(code) {
  let e = st.library.find(x => x.code === code);
  if (!e) { await reloadLibrary(true); e = st.library.find(x => x.code === code); }
  if (e) go('answer', false, e);
}
function openRequest(id) {
  const r = st.requests.find(x => x.id === id); if (!r) return;
  if (r.answered_at && !r.seen_at) { r.seen_at = new Date().toISOString(); data.markSeen(r.id); updateBadge(); }
  go('request', false, r);
}
async function send(kind, text, extra, thanksText) {
  const btn = body.querySelector('[data-a^=send],[data-a^=nm-]'); if (btn) btn.disabled = true;
  const res = await data.submitRequest({ kind, text, ctx: ctx(), extra: { ...extra, not_helped_by: extra?.not_helped_by || st.notHelpedBy || null } });
  st.notHelpedBy = null;
  if (res.ok) { st.rejected.clear(); st.leftOut = false; }   // a sent question ends the "left out" list
  if (!res.ok) {
    H()?.toast?.(T('send_failed'), 'error');
    if (btn) btn.disabled = false;
    return;
  }
  const ref = res.ref ? ` <span style="font-family:ui-monospace,monospace">${esc(res.ref)}</span>` : '';
  go('thanks', false, { html: `<b>${esc(thanksText)}</b>${ref ? `<br>${esc(T('request_number'))}${ref}` : ''}` });
  refreshRequests();
}
function lightbox(src) {
  if (!src) return;
  const lb = document.createElement('div'); lb.className = 'hh-lightbox';
  lb.innerHTML = `<button class="hh-ib" type="button" aria-label="${esc(T('close'))}">✕</button><video src="${esc(src)}" controls autoplay playsinline></video>`;
  lb.querySelector('button').onclick = () => lb.remove();
  lb.onclick = ev => { if (ev.target === lb) lb.remove(); };
  document.body.appendChild(lb);
}

async function reloadLibrary(force) {
  st.library = await data.loadLibrary(force);
  st.index = buildIndex(st.library);
  return st.library;
}

// ── boot ──
// OFF SWITCH. While false, Help makes no network calls at all, because its DB tables don't exist in production yet.
// Going live (features/help-assistant/WALKTHROUGH.md §5): apply the H1 SQL first, then flip this to true in its own PR.
// The demo sandbox turns Help on with window.__HELP_LIVE.
const HELP_LIVE = true;
// A cached-ready button that turns out to be wrong (library empty or gone) is taken away again: the flag is cleared, the buttons removed, and the shell re-rendered once if it had drawn one.
function dropButton(was) {
  clearReady();
  document.querySelectorAll('.hh-hb').forEach(b => b.remove());
  if (was) { try { H().re?.(); } catch (_) {} }
  flushReady();
}
async function boot() {
  const h = H(); if (!h?.session?.id) return 'nosession';
  if (h.session.id === 'demo') return false;
  const wasCached = cachedReady();
  const lib = await reloadLibrary(false);
  if (data.libraryMissing()) { dropButton(wasCached); return 'stop'; } // switched on before the SQL was applied: give up quietly, no retries
  if (!lib.length) { if (!data.libraryFailed?.()) dropButton(wasCached); return false; }   // library empty: keep Help hidden (a failed request keeps what was cached and retries)
  if (!panel) mount();
  st.ready = true;
  writeReady(h.session.id);
  flushReady();
  mountAll();                                                                    // in place, no re()
  if (hasSidebar() && !wasCached) { try { H().re?.(); } catch (_) {} }   // show the sidebar "?" item now that Help is live (already drawn when it was cached)
  refreshRequests();
  return true;
}
// H1 (R-20261022-0034): Help starts the moment the session exists. initHelp runs at bundle load, usually BEFORE the session is restored (the profile fetch takes a round trip); it used to
// wait 15 s before looking again, so the "?" appeared 15 s late on any real network. While there is no session it now looks every 250 ms (a property read, no request) for 30 s, then
// every 15 s as the last-resort fallback. The requests Help makes are unchanged: help_library then help_my_requests, once.
let _started = false;
export function initHelp() {
  if (!HELP_LIVE && !window.__HELP_LIVE) return;
  if (_started) return;   // one start-up chain per page (a second hud-ready must not double the requests)
  _started = true;
  let tries = 0, polls = 0;
  const attempt = async () => {
    const r = await boot();
    if (r === true || r === 'stop') return;
    if (r === 'nosession') { polls++; setTimeout(attempt, polls <= 120 ? 250 : 15000); return; }   // signed out / not restored yet: wait, without using up the 20 tries
    tries++;
    if (tries < 20) setTimeout(attempt, tries < 5 ? 1500 : 15000);
    else flushReady();
  };
  attempt();
}

window.HUD_HELP = {
  open, close, headerButton, mount: mount_,
  isReady: () => showable(),
  _refresh: () => refreshRequests(),
  // index.html hooks: the desktop sidebar "?" item and help notifications call these
  openFromNotification: ld => open({ requestId: ld?.requestId || null }),
};
